import type { KeyboardEvent } from "react";
import { LucideIcon, type LucideIconName } from "../FG/LucideIcon";

/** Icon-tile tone (the shared `fgmt-method__tile--*` tile system). */
export type MethodCardTone = "purple" | "amber" | "lime";

/**
 * W-04 — the ONE "pick a source" card primitive, shared by the wizard's Method
 * step (`Planner/wizard/steps/MethodStep.tsx`) and the MyTraining hub's
 * add-training doors (`Planner/AddTrainingModal.tsx`). One markup + one CSS
 * block (`.fgp-wz-methodcard` in planner.css); the icon tile keeps the shared
 * `fgmt-method__tile` classes both surfaces already used.
 *
 * Two interaction variants, bridged by `variant`:
 *  - `"radio"` (default) — the wizard's select card: `role="radio"` +
 *    `aria-checked`, a radio bullet, roving-tabindex friendly (`tabIndex` /
 *    `onKeyDown` pass through). One decision per screen (D-16): tapping
 *    selects AND advances; arrow keys (W-16, via the caller's roving
 *    handlers) select only. `disabled` renders a non-interactive preview.
 *    `note` renders inline in the title row (quiet uppercase).
 *  - `"nav"` — the hub's navigation door: a plain button with a trailing
 *    chevron instead of the radio bullet, hub typography via the
 *    `--nav` modifier class. `note` renders as a muted footnote below the
 *    desc (e.g. "Coming soon"); `expanded` turns the card into a disclosure
 *    (aria-expanded + chevron-down + open styling) for the hub's
 *    "Add my existing training" expander.
 */
export function MethodChoiceCard({
  variant = "radio",
  on = false,
  disabled = false,
  wide = false,
  expanded,
  icon,
  tone = "purple",
  title,
  pill,
  note,
  desc,
  tabIndex,
  onKeyDown,
  onSelect,
  className
}: {
  variant?: "radio" | "nav";
  on?: boolean;
  disabled?: boolean;
  /** nav-only: span the full grid width (`fgmt-method-grid` sections). */
  wide?: boolean;
  /** nav-only: defined ⇒ the card is a disclosure (chevron flips, aria-expanded). */
  expanded?: boolean;
  /** 38px icon tile (design 1h). */
  icon?: LucideIconName;
  tone?: MethodCardTone;
  title: string;
  /** Small emphasis pill beside the title (e.g. "Recommended"). */
  pill?: string;
  /** Muted footnote — title row (radio) / below the desc (nav). */
  note?: string;
  desc: string;
  tabIndex?: number;
  onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void;
  onSelect?: () => void;
  /** Extra modifier class(es) appended after the primitive's own (e.g. the
   *  intent step's `fgp-wz-methodcard--door` sizing). */
  className?: string;
}) {
  const nav = variant === "nav";
  const cardClassName = [
    "fgp-wz-methodcard",
    nav ? "fgp-wz-methodcard--nav" : null,
    on ? "fgp-wz-methodcard--on" : null,
    disabled ? "fgp-wz-methodcard--disabled" : null,
    wide ? "fgp-wz-methodcard--wide" : null,
    expanded ? "fgp-wz-methodcard--open" : null,
    className ?? null
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type="button"
      role={nav ? undefined : "radio"}
      aria-checked={nav ? undefined : on}
      aria-expanded={expanded}
      disabled={disabled}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      onClick={onSelect}
      className={cardClassName}>
      {!nav && (
        <span className="fgp-wz-methodcard__radio" aria-hidden>
          {on && <LucideIcon name="check" size={13} color="#fff" strokeWidth={3} />}
        </span>
      )}
      {icon && (
        <span className={`fgmt-method__tile fgmt-method__tile--${tone}`} aria-hidden>
          <LucideIcon name={icon} size={18} />
        </span>
      )}
      <span className="fgp-wz-methodcard__body">
        <span className="fgp-wz-methodcard__titlerow">
          <span className="fgp-wz-methodcard__title">{title}</span>
          {pill && <span className="fgp-wz-methodcard__pill">{pill}</span>}
          {!nav && note && <span className="fgp-wz-methodcard__note">{note}</span>}
        </span>
        <span className="fgp-wz-methodcard__desc">{desc}</span>
        {nav && note && <span className="fgp-wz-methodcard__note">{note}</span>}
      </span>
      {nav && (
        <LucideIcon
          name={expanded ? "chevron-down" : "chevron-right"}
          size={18}
          className="fgp-wz-methodcard__arrow"
        />
      )}
    </button>
  );
}
